import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './GnssConfigEditor.module.scss';

function GnssConfigEditor({ configData, onConfigChange, isEditingJson }) {
  const [enabled, setEnabled] = useState(true);
  const [setAntennaInfo, setSetAntennaInfo] = useState(false);
  const [deltaE, setDeltaE] = useState('1');
  const [deltaN, setDeltaN] = useState('2');
  const [deltaU, setDeltaU] = useState('3');
  const [antennaName, setAntennaName] = useState('TWIVSP6037L');
  const [antennaSerialNumber, setAntennaSerialNumber] = useState('0');
  const [gp1Enabled, setGp1Enabled] = useState(false);
  const [gp1Mode, setGp1Mode] = useState('Output');
  const [gp1Level, setGp1Level] = useState('LevelHigh');
  const [gp2Enabled, setGp2Enabled] = useState(false);
  const [gp2Mode, setGp2Mode] = useState('Output');
  const [gp2Level, setGp2Level] = useState('LevelLow');
  const [customSettings, setCustomSettings] = useState(['']);

  const lastConfigDataRef = useRef('');
  const isUpdatingFromGuiRef = useRef(false);

  // Generate JSON from GUI settings
  const generateConfig = useCallback(() => {
    let messages = '';

    // Add antenna info if enabled
    if (setAntennaInfo) {
      messages += `sao, all, ${deltaE}, ${deltaN}, ${deltaU}, '${antennaName}' , ${antennaSerialNumber}\r\n`;
    }

    // Add GP2 if enabled
    if (gp2Enabled) {
      messages += `sgpf, GP2, ${gp2Mode}, , ${gp2Level}\r\n`;
    }

    // Add GP1 if enabled
    if (gp1Enabled) {
      messages += `sgpf, GP1, ${gp1Mode}, , ${gp1Level}\r\n`;
    }

    // Add custom settings
    customSettings.forEach(setting => {
      if (setting.trim()) {
        messages += `${setting}\r\n`;
      }
    });

    const config = {
      enabled,
      messages,
    };

    return JSON.stringify(config, null, 2);
  }, [
    enabled,
    setAntennaInfo,
    deltaE,
    deltaN,
    deltaU,
    antennaName,
    antennaSerialNumber,
    gp1Enabled,
    gp1Mode,
    gp1Level,
    gp2Enabled,
    gp2Mode,
    gp2Level,
    customSettings,
  ]);

  // Parse JSON to GUI when configData changes from external source
  useEffect(() => {
    if (
      configData &&
      configData !== lastConfigDataRef.current &&
      !isUpdatingFromGuiRef.current &&
      !isEditingJson
    ) {
      lastConfigDataRef.current = configData;

      try {
        const config = JSON.parse(configData);
        setEnabled(config.enabled || false);

        if (config.messages) {
          const lines = config.messages.split('\r\n').filter(line => line.trim());

          // Reset states
          setSetAntennaInfo(false);
          setGp1Enabled(false);
          setGp2Enabled(false);
          const customLines = [];

          lines.forEach(line => {
            if (line.startsWith('sao,')) {
              // Parse antenna info: sao, all, 1, 2, 3, 'TWIVSP6037L' , 0
              const parts = line.split(',').map(p => p.trim());
              if (parts.length >= 7) {
                setSetAntennaInfo(true);
                setDeltaE(parts[2] || '1');
                setDeltaN(parts[3] || '2');
                setDeltaU(parts[4] || '3');
                // Remove quotes from antenna name and handle extra spaces
                const antennaNamePart = parts[5] || "'TWIVSP6037L'";
                setAntennaName(antennaNamePart.replace(/['"]/g, '').trim() || 'TWIVSP6037L');
                setAntennaSerialNumber(parts[6] || '0');
              }
            } else if (line.startsWith('sgpf, GP1,')) {
              // Parse GP1: sgpf, GP1, Output, , LevelHigh
              const parts = line.split(',').map(p => p.trim());
              if (parts.length >= 5) {
                setGp1Enabled(true);
                setGp1Mode(parts[2] || 'Output');
                setGp1Level(parts[4] || 'LevelHigh');
              }
            } else if (line.startsWith('sgpf, GP2,')) {
              // Parse GP2: sgpf, GP2, Output, , LevelLow
              const parts = line.split(',').map(p => p.trim());
              if (parts.length >= 5) {
                setGp2Enabled(true);
                setGp2Mode(parts[2] || 'Output');
                setGp2Level(parts[4] || 'LevelLow');
              }
            } else if (line.trim()) {
              // Custom setting
              customLines.push(line);
            }
          });

          setCustomSettings(customLines.length > 0 ? customLines : ['']);
        }
      } catch (error) {
        console.error('Error parsing GNSS config:', error);
      }
    }
  }, [configData, isEditingJson]);

  // Update parent component when GUI settings change
  useEffect(() => {
    // Skip the first render and when we're parsing from external config
    if (lastConfigDataRef.current) {
      isUpdatingFromGuiRef.current = true;
      const newConfig = generateConfig();
      lastConfigDataRef.current = newConfig;
      onConfigChange(newConfig);

      // Reset the flag after a short delay to allow the parent to update
      setTimeout(() => {
        isUpdatingFromGuiRef.current = false;
      }, 10);
    }
  }, [
    enabled,
    setAntennaInfo,
    deltaE,
    deltaN,
    deltaU,
    antennaName,
    antennaSerialNumber,
    gp1Enabled,
    gp1Mode,
    gp1Level,
    gp2Enabled,
    gp2Mode,
    gp2Level,
    customSettings,
    generateConfig,
    onConfigChange,
  ]);

  const addCustomSetting = () => {
    setCustomSettings([...customSettings, '']);
  };

  const removeCustomSetting = index => {
    const newSettings = customSettings.filter((_, i) => i !== index);
    setCustomSettings(newSettings.length > 0 ? newSettings : ['']);
  };

  const updateCustomSetting = (index, value) => {
    const newSettings = [...customSettings];
    newSettings[index] = value;
    setCustomSettings(newSettings);
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>GNSS Extra Configurations</h3>

      {/* Enabled checkbox */}
      <div className={styles.setting}>
        <label className={styles.label}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enabled
        </label>
      </div>

      {/* Set Antenna Info */}
      <div className={`${styles.setting} ${setAntennaInfo ? styles.settingWide : ''}`}>
        <label className={styles.label}>
          <input
            type="checkbox"
            checked={setAntennaInfo}
            onChange={e => setSetAntennaInfo(e.target.checked)}
          />
          Set Antenna Info
        </label>

        {setAntennaInfo && (
          <div className={styles.fieldGrid}>
            <label className={styles.field}>
              <span className={styles.inputLabel}>DeltaE</span>
              <input
                type="text"
                value={deltaE}
                onChange={e => setDeltaE(e.target.value)}
                className={styles.input}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>DeltaN</span>
              <input
                type="text"
                value={deltaN}
                onChange={e => setDeltaN(e.target.value)}
                className={styles.input}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>DeltaU</span>
              <input
                type="text"
                value={deltaU}
                onChange={e => setDeltaU(e.target.value)}
                className={styles.input}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>Antenna Name</span>
              <input
                type="text"
                value={antennaName}
                onChange={e => setAntennaName(e.target.value)}
                className={styles.input}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>Antenna Serial Number</span>
              <input
                type="text"
                value={antennaSerialNumber}
                onChange={e => setAntennaSerialNumber(e.target.value)}
                className={styles.input}
              />
            </label>
          </div>
        )}
      </div>

      {/* GP1 Settings */}
      <div className={styles.setting}>
        <label className={styles.label}>
          <input
            type="checkbox"
            checked={gp1Enabled}
            onChange={e => setGp1Enabled(e.target.checked)}
          />
          GP1
        </label>

        {gp1Enabled && (
          <div className={styles.fieldGrid}>
            <label className={styles.field}>
              <span className={styles.inputLabel}>Mode</span>
              <select
                value={gp1Mode}
                onChange={e => setGp1Mode(e.target.value)}
                className={styles.select}
              >
                <option value="Input">Input</option>
                <option value="Output">Output</option>
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>Level</span>
              <select
                value={gp1Level}
                onChange={e => setGp1Level(e.target.value)}
                className={styles.select}
              >
                <option value="LevelLow">LevelLow</option>
                <option value="LevelHigh">LevelHigh</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {/* GP2 Settings */}
      <div className={styles.setting}>
        <label className={styles.label}>
          <input
            type="checkbox"
            checked={gp2Enabled}
            onChange={e => setGp2Enabled(e.target.checked)}
          />
          GP2
        </label>

        {gp2Enabled && (
          <div className={styles.fieldGrid}>
            <label className={styles.field}>
              <span className={styles.inputLabel}>Mode</span>
              <select
                value={gp2Mode}
                onChange={e => setGp2Mode(e.target.value)}
                className={styles.select}
              >
                <option value="Input">Input</option>
                <option value="Output">Output</option>
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.inputLabel}>Level</span>
              <select
                value={gp2Level}
                onChange={e => setGp2Level(e.target.value)}
                className={styles.select}
              >
                <option value="LevelLow">LevelLow</option>
                <option value="LevelHigh">LevelHigh</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {/* Custom Settings */}
      <div className={`${styles.setting} ${styles.settingFull}`}>
        <div className={styles.listHeader}>
          <div className={styles.label}>Custom Commands/Settings</div>
          <button type="button" onClick={addCustomSetting} className={styles.addButton}>
            + Add Custom Setting
          </button>
        </div>

        <div className={styles.customSettingList}>
          {customSettings.map((setting, index) => (
            <div key={index} className={styles.customSettingRow}>
              <input
                type="text"
                value={setting}
                onChange={e => updateCustomSetting(index, e.target.value)}
                className={styles.input}
                aria-label={`Custom setting ${index + 1}`}
              />
              {customSettings.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCustomSetting(index)}
                  className={styles.removeButton}
                  aria-label={`Remove custom setting ${index + 1}`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default GnssConfigEditor;
